/**
 * Workflow action buttons for the call-off detail modal footer.
 *
 * Extracted from `CallOffDetailModal` (CR-7367), which was over the 500-line
 * cap before this change and would have grown further. The mixed-load rules
 * below also belong together rather than inline among the modal's JSX: which
 * ASN a button raises, and when the load may be called dispatched, are two
 * halves of one decision.
 *
 * Mixed loads (`destination_type === 'mixed'`) ship as two separate ASNs — one
 * for the warehouse lines, one for the direct lines. Everything conditional on
 * `isMixed` exists because a single Create-ASN button cannot express that: it
 * pre-selected every line regardless of destination, and the first submission
 * auto-dispatched the whole call-off, hiding the button before the second load
 * had been raised at all.
 */
import { useNavigate } from 'react-router-dom';
import { Send, CheckCircle, Truck, XCircle, Pencil, Ship, Loader2 } from 'lucide-react';
import type { CallOff } from '../../../lib/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

export type CallOffAction = 'send' | 'confirm' | 'dispatch' | 'cancel';

/** The two destinations a call-off line can carry, in display order. */
const DELIVERY_SIDES = ['warehouse', 'direct'] as const;
type DeliverySide = (typeof DELIVERY_SIDES)[number];

const SIDE_LABEL: Record<DeliverySide, string> = {
  warehouse: 'Warehouse',
  direct: 'Direct',
};

/**
 * The destinations this call-off actually has lines for, in display order.
 *
 * Derived from the lines rather than trusted from `destination_type`, so a
 * mixed call-off whose direct lines were all removed during editing offers one
 * button instead of a dead second one.
 *
 * @param callOff - The call-off being viewed.
 * @returns The sides present on its lines.
 */
function sidesOnCallOff(callOff: CallOff): DeliverySide[] {
  const present = new Set(callOff.lines.map((l) => l.delivery_type));
  return DELIVERY_SIDES.filter((side) => present.has(side));
}

/**
 * The destinations that already have an ASN raised against them.
 *
 * A cancelled ASN leaves its side unshipped — the supplier has to be able to
 * raise a replacement, which is the reason these buttons are per-side at all.
 * A legacy ASN carrying both kinds of line covers both sides.
 *
 * @param callOff - The call-off being viewed.
 * @returns The sides already covered by a live ASN.
 */
function sidesShipped(callOff: CallOff): Set<DeliverySide> {
  const shipped = new Set<DeliverySide>();
  for (const asn of callOff.asns || []) {
    if (asn.status === 'cancelled' || !asn.delivery_option) continue;
    if (asn.delivery_option === 'mixed') {
      DELIVERY_SIDES.forEach((side) => shipped.add(side));
    } else {
      shipped.add(asn.delivery_option);
    }
  }
  return shipped;
}

interface CallOffDetailActionsProps {
  callOff: CallOff;
  isCustomer: boolean;
  isSupplier: boolean;
  /** A workflow request is in flight — disables every action. */
  actionLoading: boolean;
  canEdit: boolean;
  supplierCanEditLines: boolean;
  /** Set when the customer may reopen the call-off in the create wizard. */
  canEditInWizard: boolean;
  onAction: (action: CallOffAction, skipValidation?: boolean, reason?: string) => void;
  onEditInWizard: () => void;
  onEnterInlineEdit: () => void;
  onClose: () => void;
}

export default function CallOffDetailActions({
  callOff, isCustomer, isSupplier, actionLoading, canEdit, supplierCanEditLines,
  canEditInWizard, onAction, onEditInWizard, onEnterInlineEdit, onClose,
}: CallOffDetailActionsProps) {
  const navigate = useNavigate();

  const isMixed = callOff.destination_type === 'mixed';
  const shipped = sidesShipped(callOff);
  const outstandingSides = sidesOnCallOff(callOff).filter((side) => !shipped.has(side));
  const isConfirmed = callOff.detail_status === 'confirmed';
  // A mixed load stays confirmed until every side has an ASN, so one half can
  // already be on the road. Cancelling then would free that half's PO while the
  // goods travel — the backend refuses it, so the button is not offered.
  const hasShippedAsn = (callOff.asns || []).some(
    (asn) => asn.status !== 'created' && asn.status !== 'cancelled',
  );

  /**
   * Open the ASN wizard for this call-off, optionally narrowed to one
   * destination so only that side's delivery lines arrive pre-selected.
   */
  const openAsnWizard = (side?: DeliverySide) => {
    onClose();
    const sideParam = side ? `&delivery_option=${side}` : '';
    navigate(`/purchasing?tab=asn&action=create&call_off=${callOff.id}${sideParam}`);
  };

  const promptCancel = () => {
    const reason = window.prompt(
      'Reason for cancelling this call-off (recorded on the call-off and visible to the supplier if already sent):'
    );
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error('A cancellation reason is required.');
      return;
    }
    onAction('cancel', false, trimmed);
  };

  return (
    <>
      {isCustomer && callOff.detail_status === 'draft' && (
        <Button size="sm" className="gap-1.5" onClick={() => onAction('send')} disabled={actionLoading}>
          <Send className="h-3.5 w-3.5" /> Send to Supplier
        </Button>
      )}
      {isCustomer && ['draft', 'pending', 'confirmed'].includes(callOff.detail_status) && !hasShippedAsn && (
        <Button
          size="sm"
          variant="destructive"
          className="gap-1.5"
          onClick={promptCancel}
          disabled={actionLoading}
        >
          <XCircle className="h-3.5 w-3.5" /> Cancel
        </Button>
      )}
      {isCustomer && canEdit && canEditInWizard && (
        <Button variant="outline" size="sm" className="gap-1.5" onClick={onEditInWizard}>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </Button>
      )}
      {isSupplier && callOff.detail_status === 'pending' && (
        <Button size="sm" className="gap-1.5" onClick={() => onAction('confirm')} disabled={actionLoading}>
          <CheckCircle className="h-3.5 w-3.5" /> Confirm
        </Button>
      )}

      {/* CR-7367: a mixed load raises one ASN per destination, so it gets one
          button per destination still outstanding. Each disappears on its own
          once that side has an ASN — raising the warehouse load must not take
          the direct button with it, which is what the single button did. */}
      {isSupplier && isConfirmed && isMixed && outstandingSides.map((side) => (
        <Button
          key={side}
          size="sm"
          className="gap-1.5"
          onClick={() => openAsnWizard(side)}
          disabled={actionLoading}
        >
          <Ship className="h-3.5 w-3.5" /> Create ASN for {SIDE_LABEL[side]}
        </Button>
      ))}

      {/* CR-7367: dispatch is manual for a mixed load, and only once every side
          has shipped. The automatic first-ASN-wins dispatch is what marked a
          half-shipped load as gone; the supplier says when it is actually
          away. Non-mixed loads keep dispatching automatically on ASN submit,
          so they never show this. */}
      {isSupplier && isConfirmed && isMixed && outstandingSides.length === 0 && (
        <Button size="sm" className="gap-1.5" onClick={() => onAction('dispatch')} disabled={actionLoading}>
          <Truck className="h-3.5 w-3.5" /> Mark Dispatched
        </Button>
      )}
      {isSupplier && isConfirmed && isMixed && outstandingSides.length > 0 && (
        <span className="text-xs text-muted-foreground">
          Raise an ASN for each destination, then mark the call-off dispatched.
        </span>
      )}

      {isSupplier && isConfirmed && !isMixed && (
        <>
          <Button size="sm" className="gap-1.5" onClick={() => openAsnWizard()} disabled={actionLoading}>
            <Ship className="h-3.5 w-3.5" /> Create ASN
          </Button>
          <span className="text-xs text-muted-foreground">
            Submit the ASN to mark this call-off as dispatched.
          </span>
        </>
      )}

      {/* CR-7367: the customer's "Mark Dispatched" button is gone. It rendered
          at 'confirmed' for the customer, but dispatch_call_off rejects anyone
          who is not the supplier — so the only thing it could do was 403. */}

      {supplierCanEditLines && (
        <Button variant="outline" size="sm" className="gap-1.5" onClick={onEnterInlineEdit}>
          <Pencil className="h-3.5 w-3.5" /> Edit Call-Off
        </Button>
      )}
      {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
    </>
  );
}
